import { BaseTimePeriod, TimePeriod } from '@services/FiltersTypes';
import { RelativePeriod } from '@components/comparison-period-selector/Types';
import { PeriodSet } from './PeriodSet';
import { getLatestPeriodCombo } from './PeriodSetFallback';
import { useUpdatePerfPeriodSet } from './UseUpdatePerfPeriodSet';
import { useSelector } from 'react-redux';
import { IRootState } from '@state/reducers/Reducers';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import {
  getDefaultCustomMonthsPeriod,
  getDefaultCustomWeeksPeriod,
  isValidCustomPeriodSelections,
} from '../PeriodUtils';
import { CustomMonthsPeriodState, CustomWeeksPeriodState } from '@state/reducers/main-filter/MainFiltersReducer';

export type CustomMonthsPeriod = {
  period: BaseTimePeriod;
  comparisonPeriod: BaseTimePeriod;
};

export type CustomWeeksPeriod = {
  period: BaseTimePeriod;
  comparisonPeriod: BaseTimePeriod;
};

export type BuildValidPeriodSetConfig = {
  period: BaseTimePeriod;
  periodicity: Periodicity;
  comparisonPeriod: BaseTimePeriod | RelativePeriod;
  customMonths: CustomMonthsPeriodState;
  customWeeks: CustomWeeksPeriodState;
  timePeriodOption: TimePeriod;
  monthsDefault: CustomMonthsPeriod;
  weeksDefault: CustomWeeksPeriod;
};

export const useInitializedPeriodSet = (timePeriodOptions: TimePeriod, useMarketPremiumSelector = false): PeriodSet => {
  const mainFilterState = useSelector((state: IRootState) =>
    useMarketPremiumSelector ? state.mainFiltersMarketPremium : state.mainFilters,
  );
  const period = mainFilterState.period;
  const periodicity = mainFilterState.timeFrame;
  const comparisonPeriod = mainFilterState.comparisonPeriod;
  const customMonthsPeriodState = mainFilterState?.customMonthsPeriodState;
  const customWeeksPeriodState = mainFilterState?.customWeeksPeriodState;

  const {
    updatePeriod,
    updatePeriodicity,
    updateComparisonPeriod,
    updateCustomMonthsPeriodState,
    updateCustomWeeksPeriodState,
  } = useUpdatePerfPeriodSet();

  const defaultComparisonPeriod = RelativePeriod.PreviousYearCurrentPeriod;
  const customMonthsDefaultPeriod: CustomMonthsPeriod = getDefaultCustomMonthsPeriod(timePeriodOptions);
  const customWeeksDefaultPeriod: CustomWeeksPeriod = getDefaultCustomWeeksPeriod(timePeriodOptions);

  const buildDefaultPeriodSet = (): PeriodSet => {
    const initialPeriodicity =
      timePeriodOptions.monthly === null || timePeriodOptions.monthly.length === 0
        ? Periodicity.Weekly
        : Periodicity.Monthly;

    const newCombo = getLatestPeriodCombo(
      timePeriodOptions,
      initialPeriodicity,
      null,
      defaultComparisonPeriod,
      customMonthsDefaultPeriod,
      customWeeksDefaultPeriod,
    );

    const newSet: PeriodSet = {
      ...newCombo,
      comparisonPeriod: defaultComparisonPeriod,
      customMonthsPeriodState: customMonthsDefaultPeriod,
      customWeeksPeriodState: customWeeksDefaultPeriod,
    };

    updatePeriod(newSet.period);
    updatePeriodicity(newSet.periodicity);
    updateComparisonPeriod(newSet.comparisonPeriod);
    updateCustomMonthsPeriodState(newSet.customMonthsPeriodState);
    updateCustomWeeksPeriodState(newSet.customWeeksPeriodState);

    return newSet;
  };

  const buildValidatedPeriodSet = (config: BuildValidPeriodSetConfig): PeriodSet => {
    const {
      period: configPeriod,
      periodicity: configPeriodicity,
      comparisonPeriod: configComparisonPeriod,
      customMonths,
      customWeeks,
      timePeriodOption,
      monthsDefault,
      weeksDefault,
    } = config;

    let finalPeriod = configPeriod;
    if (configPeriod.value.includes('YTD') || configPeriod.value.includes('MAT')) {
      const updatedPeriodValue = handleVersionSwitch(configPeriod, configPeriodicity, timePeriodOption);
      if (updatedPeriodValue) {
        finalPeriod = { ...configPeriod, value: updatedPeriodValue };
      }
    }

    const isToRetainCustomMonths = isValidCustomPeriodSelections(customMonths, timePeriodOption.customMonths);
    const isToRetainCustomWeeks = isValidCustomPeriodSelections(customWeeks, timePeriodOption.customWeeks);

    return {
      period: finalPeriod,
      periodicity: configPeriodicity,
      comparisonPeriod: configComparisonPeriod,
      customMonthsPeriodState: isToRetainCustomMonths ? customMonths : monthsDefault,
      customWeeksPeriodState: isToRetainCustomWeeks ? customWeeks : weeksDefault,
    };
  };

  if (
    [period, periodicity, comparisonPeriod, customMonthsPeriodState, customWeeksPeriodState].some(
      (item) => item === null,
    )
  ) {
    return buildDefaultPeriodSet();
  }

  return buildValidatedPeriodSet({
    period: period as BaseTimePeriod,
    periodicity: periodicity as Periodicity,
    comparisonPeriod: comparisonPeriod as BaseTimePeriod | RelativePeriod,
    customMonths: customMonthsPeriodState!,
    customWeeks: customWeeksPeriodState!,
    timePeriodOption: timePeriodOptions,
    monthsDefault: customMonthsDefaultPeriod,
    weeksDefault: customWeeksDefaultPeriod,
  });
};

/**
 * Handle YTD and MAT value when switching v2 and v3 or vice versa
 * Issue only happen when switching to another version, where both v2 and v3 have same period.label but different period.value:
 * e.g. v2: label: YTD Aug 24 value: YTD
 *      v3: label: YTD Aug 24 value: YTD Aug 24
 * This is because getFallbackPeriodicity return null since it compare using period.label instead of period.value.
 * Hence, additional checking is done if period.value is not same between two version.
 */

const handleVersionSwitch = (
  period: BaseTimePeriod,
  periodicity: Periodicity,
  timePeriodOptions: TimePeriod,
): string | undefined => {
  const key: keyof TimePeriod = periodicity === Periodicity.Monthly ? 'monthly' : 'weekly';

  const periodExist = timePeriodOptions[key].find((fil) => fil.value === period?.value);
  if (!periodExist) {
    const updatedPeriodValue = timePeriodOptions[key].find((tp) => tp.label.includes(period?.label));
    return updatedPeriodValue?.value;
  }

  return undefined;
};
